import { memo } from 'react';
import type { Edge, EdgeProps } from '@xyflow/react';
import { cn } from '@/lib/utils';
import type { LaidOutEdge } from './sideTree';
import { branchTone } from './MindmapNode';

export type MindmapFlowEdge = Edge<
  LaidOutEdge & {
    dimmed: boolean;
    animate: boolean;
    ghost?: boolean;
    /** A preview's draw delay (ms), replacing the map's own stagger. */
    delay?: number;
  },
  'mindmap'
>;

/** A preview's line to a hinted node: strong at its parent, faint at it. */
const GHOST_FROM = 0.42;
const GHOST_TO = 0.06;

interface Segment {
  sx: number;
  sy: number;
  tx: number;
  ty: number;
}

/**
 * One vertical S-curve between two steps. Both control points sit halfway
 * down, directly below the start and above the end, so the path leaves and
 * enters each pill vertically and the sideways move between the offset steps
 * happens smoothly in the gap.
 */
function spinePath({ sx, sy, tx, ty }: Segment) {
  const my = (sy + ty) / 2;
  return `M${sx},${sy} C${sx},${my} ${tx},${my} ${tx},${ty}`;
}

function edgeStyle(spine: boolean, branch: number, depth: number) {
  if (spine) {
    // Spine segment into step k draws first, then that step's branches.
    return {
      width: 1.75,
      opacity: 0.45,
      tone: 'text-brand-500',
      delay: branch * 90,
    };
  }
  return {
    width: 1.25,
    opacity: depth <= 2 ? 0.42 : 0.3,
    tone: branchTone(branch).stroke,
    delay: branch * 90 + 60 + (depth - 1) * 50,
  };
}

/**
 * Two kinds of line:
 *
 * - `tree`: a soft horizontal S-curve from a parent's outer side to a child's
 *   inner side, in the branch colour. Control points sit at the horizontal
 *   midpoint, so every edge leaves and arrives level and a busy column reads
 *   as rows. Opacity follows the marketing mindmap: 0.42 into a concept's
 *   first column, 0.30 deeper.
 * - `spine`: the roadmap path from one step's bottom to the next step's top,
 *   slightly heavier and in brand indigo so it reads as the route rather than
 *   another branch. The steps sit alternately right and left of centre, and
 *   each segment is one gentle S-curve between them.
 *
 * `non-scaling-stroke` keeps both crisp at every zoom level.
 */
function MindmapEdgeView({
  sourceX,
  sourceY,
  targetX,
  targetY,
  data,
}: EdgeProps<MindmapFlowEdge>) {
  const spine = data?.kind === 'spine';
  const style = edgeStyle(spine, data?.branch ?? 0, data?.depth ?? 1);
  const midX = (sourceX + targetX) / 2;
  const d = spine
    ? spinePath({ sx: sourceX, sy: sourceY, tx: targetX, ty: targetY })
    : `M${sourceX},${sourceY} C${midX},${sourceY} ${midX},${targetY} ${targetX},${targetY}`;
  return (
    <path
      d={d}
      fill='none'
      stroke='currentColor'
      strokeWidth={style.width}
      strokeOpacity={style.opacity}
      vectorEffect='non-scaling-stroke'
      pathLength={1}
      style={
        data?.animate
          ? { animationDelay: `${data.delay ?? style.delay}ms` }
          : undefined
      }
      className={cn(
        style.tone,
        'transition-opacity duration-150',
        data?.animate && 'mindmap-draw',
        data?.dimmed && 'opacity-25'
      )}
    />
  );
}

/**
 * A preview's line toward a hinted node: the same curve, fading from the last
 * clear node to almost nothing at the faint pill it leads to - "more here",
 * without saying what.
 */
function GhostEdge({
  id,
  sx,
  sy,
  tx,
  ty,
  branch,
  draw,
}: {
  id: string;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  branch: number;
  /** Draw-in delay (ms), or null for no entrance. */
  draw: number | null;
}) {
  const gradient = `ghost-${id}`;
  const midX = (sx + tx) / 2;
  const tone = branchTone(branch).stroke;
  return (
    <g className={tone}>
      <defs>
        <linearGradient
          id={gradient}
          gradientUnits='userSpaceOnUse'
          x1={sx}
          y1={sy}
          x2={tx}
          y2={ty}
        >
          <stop offset='0%' stopColor='currentColor' stopOpacity={GHOST_FROM} />
          <stop offset='100%' stopColor='currentColor' stopOpacity={GHOST_TO} />
        </linearGradient>
      </defs>
      <path
        d={`M${sx},${sy} C${midX},${sy} ${midX},${ty} ${tx},${ty}`}
        fill='none'
        stroke={`url(#${gradient})`}
        strokeWidth={1.25}
        strokeLinecap='round'
        pathLength={1}
        style={draw !== null ? { animationDelay: `${draw}ms` } : undefined}
        className={cn(draw !== null && 'mindmap-draw')}
      />
    </g>
  );
}

function MindmapEdgeOrGhost(props: EdgeProps<MindmapFlowEdge>) {
  const { id, sourceX, sourceY, targetX, targetY, data } = props;
  if (!data?.ghost) return <MindmapEdgeView {...props} />;
  return (
    <GhostEdge
      id={id}
      sx={sourceX}
      sy={sourceY}
      tx={targetX}
      ty={targetY}
      branch={data.branch}
      draw={data.animate ? (data.delay ?? 0) : null}
    />
  );
}

export const MindmapEdgeComponent = memo(MindmapEdgeOrGhost);
